import type { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ColoredTag from 'modules/org_chart/components/ColoredTag'
import { useDeletePosition } from 'modules/org_chart/hooks/useDeletePosition'
import { useJobPosition } from 'modules/org_chart/hooks/useJobPosition'
import type { PositionNodeData } from 'modules/org_chart/models/NodeData'

import './index.styles.css'
interface Props {
  data: PositionNodeData
  x: number
  y: number
  refetch: () => void
}

const AddPosition: FC<Props> = ({ data, x, y, refetch }) => {
  const { loading, jobPosition } = useJobPosition(data.positionId)
  const { loading: mutating, delete: deletePosition } = useDeletePosition()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const jobTitle = useUnsafeMemo<string>(() => {
    if (!jobPosition) return ''

    const level = jobPosition.jobCatalogLevel

    if (level) {
      return [level.isDefault ? '' : level.name, level.role?.name].filter(Boolean).join(' ')
    } else {
      return jobPosition.jobPosting?.title as string
    }
  }, [jobPosition])

  const handleNavigateToJobPosting = () => {
    if (!jobPosition) return
    track(trackingEvents.orgChart.nodes.position.navigate)

    navigate(
      navigation.ats.jobs.jobsId.applications.fullPath({
        jobId: jobPosition?.jobPosting?.id as number,
      })
    )
  }

  const handleDeletePosition = async () => {
    if (!jobPosition) return

    try {
      await deletePosition(jobPosition.id)

      track(trackingEvents.orgChart.nodes.position.remove, {
        is_successful: true,
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
        time: 2000,
      })

      refetch()
    } catch (error) {
      track(trackingEvents.orgChart.nodes.position.remove, {
        is_successful: false,
        error: String(error),
      })
    }
  }

  if (loading) return null

  return (
    <>
      <foreignObject style={{ overflow: 'visible' }} width={236} height={258} x={x} y={y}>
        <PopoverMenu type="list">
          <Box alignSelf="center" flexDirection="column" alignItems="center" gap="s8">
            <Box flexDirection="column" alignItems="center" gap="s2">
              <PopoverMenu.Trigger disabled={mutating}>
                <Box
                  width="s80"
                  height="s80"
                  borderRadius={{ all: 'rel333' }}
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  className={'Position_node'}
                >
                  <Icon.Medium icon="search" color="grey800" />
                </Box>
              </PopoverMenu.Trigger>
              <Text.Normal size="300" weight="bold" color="grey1000">
                ...
              </Text.Normal>
              <Text.Normal size="100" weight="normal" color="grey1000" ellipsis>
                {jobTitle}
              </Text.Normal>
            </Box>
            <ColoredTag>{jobPosition?.jobPosting?.location?.name || ''}</ColoredTag>
          </Box>
          <PopoverMenu.Content side="top" align="center">
            <PopoverMenu.TouchableOption
              title={i18n.t('employees.orgchart.go_to_job_opening')}
              onClick={handleNavigateToJobPosting}
            />
            <PopoverMenu.TouchableOption
              title={i18n.t('employees.orgchart.remove_vacant_position')}
              onClick={handleDeletePosition}
              danger
            />
          </PopoverMenu.Content>
        </PopoverMenu>
      </foreignObject>
    </>
  )
}

export default observer(AddPosition)
